import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import emailDeliveryAnalysisService from '../../services/emailDeliveryAnalysisService';
import EmailDeliverySummaryCard from './components/EmailDeliverySummaryCard';

import EmailFilterPanel from './components/EmailFilterPanel';
import DeliveryAnalyticsPanel from './components/DeliveryAnalyticsPanel';
import AIAnalysisPanel from './components/AIAnalysisPanel';
import CorrectiveActionsModal from './components/CorrectiveActionsModal';
import ContentSuggestionsModal from './components/ContentSuggestionsModal';

const InvoiceEmailMonitoringDeliveryDashboard = () => {
  const [activeTab, setActiveTab] = useState('email_logs');
  const [emails, setEmails] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [correctiveActionsModal, setCorrectiveActionsModal] = useState({ open: false, email: null, actions: null });
  const [contentSuggestionsModal, setContentSuggestionsModal] = useState({ open: false, suggestions: null });
  const [filters, setFilters] = useState({
    deliveryStatus: '',
    dateFrom: '',
    dateTo: '',
    recipientEmail: '',
    searchTerm: ''
  });

  useEffect(() => {
    loadData();
    const channel = subscribeToEmailUpdates();
    return () => {
      if (channel) {
        supabase?.removeChannel(channel);
      }
    };
  }, []);

  useEffect(() => {
    loadEmails();
  }, [filters]);

  const subscribeToEmailUpdates = () => {
    const channel = supabase
      ?.channel('invoice-email-updates')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_queue',
          filter: 'notification_type=eq.compliance_alert'
        },
        (payload) => {
          loadData();
        }
      )
      ?.subscribe();
    return channel;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [emailsData, statsData] = await Promise.all([
        loadEmails(),
        loadStatistics()
      ]);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmails = async () => {
    try {
      let query = supabase
        ?.from('notification_queue')
        ?.select(`
          *,
          notification_delivery_log(
            id,
            delivery_channel,
            delivery_status,
            delivered_at,
            error_details
          )
        `)
        ?.eq('notification_type', 'compliance_alert')
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (filters?.deliveryStatus) {
        query = query?.eq('notification_status', filters?.deliveryStatus);
      }

      if (filters?.recipientEmail) {
        query = query?.ilike('recipient_email', `%${filters?.recipientEmail}%`);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      setEmails(data || []);
      return data || [];
    } catch (error) {
      console.error('Error loading emails:', error);
      return [];
    }
  };

  const loadStatistics = async () => {
    try {
      const { data: allEmails, error } = await supabase
        ?.from('notification_queue')
        ?.select('notification_status, created_at, sent_at, delivered_at')
        ?.eq('notification_type', 'compliance_alert');

      if (error) throw error;

      const stats = {
        totalSent: allEmails?.length || 0,
        delivered: allEmails?.filter(e => e?.notification_status === 'delivered')?.length || 0,
        failed: allEmails?.filter(e => e?.notification_status === 'failed')?.length || 0,
        pending: allEmails?.filter(e => e?.notification_status === 'pending' || e?.notification_status === 'sent')?.length || 0
      };

      // Calculate average delivery time
      const deliveredEmails = allEmails?.filter(e => e?.delivered_at && e?.sent_at) || [];
      if (deliveredEmails?.length > 0) {
        const totalTime = deliveredEmails?.reduce((sum, email) => {
          const sentTime = new Date(email?.sent_at)?.getTime();
          const deliveredTime = new Date(email?.delivered_at)?.getTime();
          return sum + (deliveredTime - sentTime);
        }, 0);
        stats.avgDeliveryTime = Math.round(totalTime / deliveredEmails?.length / 1000); // in seconds
      } else {
        stats.avgDeliveryTime = 0;
      }

      stats.successRate = stats?.totalSent > 0 ? ((stats?.delivered / stats?.totalSent) * 100)?.toFixed(1) : 0;

      setStatistics(stats);
      return stats;
    } catch (error) {
      console.error('Error loading statistics:', error);
      return null;
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleEmailSelect = (email) => {
    setSelectedEmail(email);
  };

  const handleAnalyzeWithAI = async () => {
    try {
      setAiLoading(true);
      const failedEmails = emails?.filter(e => e?.notification_status === 'failed') || [];
      
      if (failedEmails?.length === 0) {
        alert('No failed emails to analyze');
        return;
      }

      const analysis = await emailDeliveryAnalysisService?.analyzeDeliveryFailures(failedEmails);
      setAiAnalysis(analysis);
    } catch (error) {
      alert(error?.message || 'Failed to analyze emails with AI');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateCorrectiveActions = async (email) => {
    try {
      setCorrectiveActionsModal({ open: true, email, actions: null });
      const actions = await emailDeliveryAnalysisService?.generateCorrectiveActions(email);
      setCorrectiveActionsModal({ open: true, email, actions });
    } catch (error) {
      alert(error?.message || 'Failed to generate corrective actions');
      setCorrectiveActionsModal({ open: false, email: null, actions: null });
    }
  };

  const handleSuggestContentFixes = async () => {
    try {
      setContentSuggestionsModal({ open: true, suggestions: null });
      const failedEmails = emails?.filter(e => e?.notification_status === 'failed') || [];
      
      const emailContent = {
        subject: 'Invoice Request',
        body: 'Standard invoice request email content'
      };
      
      const failureContext = {
        totalFailures: failedEmails?.length,
        commonErrors: failedEmails?.map(e => e?.error_message)?.filter(Boolean)
      };

      const suggestions = await emailDeliveryAnalysisService?.suggestContentFixes(emailContent, failureContext);
      setContentSuggestionsModal({ open: true, suggestions });
    } catch (error) {
      alert(error?.message || 'Failed to generate content suggestions');
      setContentSuggestionsModal({ open: false, suggestions: null });
    }
  };

  const handleRetryEmail = async (emailId) => {
    try {
      const { error } = await supabase
        ?.from('notification_queue')
        ?.update({ 
          notification_status: 'pending',
          retry_count: supabase?.raw('retry_count + 1')
        })
        ?.eq('id', emailId);

      if (error) throw error;
      loadData();
    } catch (error) {
      console.error('Error retrying email:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      delivered: 'text-green-600 bg-green-50',
      sent: 'text-blue-600 bg-blue-50',
      pending: 'text-yellow-600 bg-yellow-50',
      failed: 'text-red-600 bg-red-50'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Invoice Email Monitoring & Delivery Dashboard - PNG HR Analytics</title>
      </Helmet>

      <div className="min-h-screen bg-neutral-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">Invoice Email Monitoring & Delivery Dashboard</h1>
              <p className="text-neutral-600 mt-1">Track invoice request emails with delivery status and AI-powered debugging</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSuggestContentFixes}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Icon name="FileText" size={18} />
                Content Suggestions
              </button>
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="RefreshCw" size={18} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <EmailDeliverySummaryCard
            title="Total Emails Sent"
            value={statistics?.totalSent || 0}
            icon="Mail"
            color="blue"
            subtitle=""
          />
          <EmailDeliverySummaryCard
            title="Successfully Delivered"
            value={statistics?.delivered || 0}
            icon="CheckCircle"
            color="green"
            subtitle={`${statistics?.successRate || 0}% success rate`}
          />
          <EmailDeliverySummaryCard
            title="Failed Deliveries"
            value={statistics?.failed || 0}
            icon="XCircle"
            color="red"
            subtitle=""
          />
          <EmailDeliverySummaryCard
            title="Avg Delivery Time"
            value={`${statistics?.avgDeliveryTime || 0}s`}
            icon="Clock"
            color="purple"
            subtitle=""
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-neutral-200">
            <div className="flex gap-4 px-6">
              <button
                onClick={() => setActiveTab('email_logs')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'email_logs' ?'border-blue-600 text-blue-600' :'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="List" size={18} />
                  Email Logs
                </div>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'analytics' ?'border-blue-600 text-blue-600' :'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="BarChart3" size={18} />
                  Delivery Analytics
                </div>
              </button>
              <button
                onClick={() => setActiveTab('ai_analysis')}
                className={`py-4 px-2 border-b-2 font-medium transition-colors ${
                  activeTab === 'ai_analysis' ?'border-blue-600 text-blue-600' :'border-transparent text-neutral-600 hover:text-neutral-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name="Sparkles" size={18} />
                  AI Analysis
                </div>
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'email_logs' && (
              <div className="space-y-6">
                <EmailFilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Timestamp
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Delivery Channel
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Failure Reason
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Retry Count
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-neutral-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {emails?.map((email) => (
                        <tr
                          key={email?.id}
                          className="hover:bg-neutral-50 cursor-pointer transition-colors"
                          onClick={() => handleEmailSelect?.(email)}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-900">
                            {email?.created_at ? new Date(email?.created_at)?.toLocaleString() : 'N/A'}
                          </td>
                          <td className="px-4 py-4 text-sm text-neutral-900">
                            <div className="flex items-center gap-2">
                              <Icon name="Mail" size={16} className="text-neutral-400" />
                              {email?.recipient_email || 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-neutral-900 max-w-xs truncate">
                            {email?.subject || 'Invoice Request'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor?.(email?.notification_status)}`}>
                              {email?.notification_status || 'unknown'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-600">
                            {email?.notification_delivery_log?.[0]?.delivery_channel || 'email'}
                          </td>
                          <td className="px-4 py-4 text-sm text-neutral-600 max-w-xs truncate">
                            {email?.notification_status === 'failed' ? (email?.error_message || email?.notification_delivery_log?.[0]?.error_details || 'Unknown error') : 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-neutral-600">
                            {email?.retry_count || 0}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              {email?.notification_status === 'failed' && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      handleRetryEmail?.(email?.id);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                  >
                                    <Icon name="RefreshCw" size={14} />
                                    Retry
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      handleGenerateCorrectiveActions(email);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                                  >
                                    <Icon name="Sparkles" size={14} />
                                    AI Fix
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <DeliveryAnalyticsPanel
                emails={emails}
                statistics={statistics}
              />
            )}

            {activeTab === 'ai_analysis' && (
              <AIAnalysisPanel
                analysis={aiAnalysis}
                loading={aiLoading}
                onAnalyze={handleAnalyzeWithAI}
              />
            )}
          </div>
        </div>

        {/* Modals */}
        {correctiveActionsModal?.open && (
          <CorrectiveActionsModal
            email={correctiveActionsModal?.email}
            correctiveActions={correctiveActionsModal?.actions}
            onClose={() => setCorrectiveActionsModal({ open: false, email: null, actions: null })}
            loading={!correctiveActionsModal?.actions}
          />
        )}

        {contentSuggestionsModal?.open && (
          <ContentSuggestionsModal
            contentSuggestions={contentSuggestionsModal?.suggestions}
            onClose={() => setContentSuggestionsModal({ open: false, suggestions: null })}
            loading={!contentSuggestionsModal?.suggestions}
          />
        )}
      </div>
    </>
  );
};

export default InvoiceEmailMonitoringDeliveryDashboard;